import React, { useState, useEffect } from "react";
import { ComposableMap, Geographies, Geography } from "react-simple-maps";
import { scaleQuantize } from "d3-scale";
import styled from "styled-components";

import microApi from "../helpers/microApi";
import { getWhens, percentChange, prettyPercent } from "../helpers/utils";

const getColorScale = values => {
  const min = Math.min(...values);
  const max = Math.max(...values.map(Math.abs));
  return scaleQuantize()
    .domain([min >= 0 ? 0 : -max, max])
    .range(min >= 0
      ? [
        "#EDF3FF",
        "#E7FDFE",
        "#B8CFFF",
        "#83ACFF",
        "#4D88FF",
        "#3E6DCC",
        "#2E5299",
        "#1F3666"
      ] : [
        "#93293B",
        "#F54562",
        "#F87D91",
        "#FBB5C0",
        "#FEECEF",
        "#d1e5f0",
        "#92c5de",
        "#4393c3",
        "#2166ac",
        "#053061"
      ]
    );
};

const ToggleWrapper = styled.div`
  font-size: 0.9rem;
  text-align: center;

  @media only screen and (min-width: 749px) {
    position: absolute;
    top: 2%;
    left: 0;
    right: 0;
  }

  select {
    -moz-appearance: none;
    -webkit-appearance: none;
    appearance: none;
    background: none;
    border: 0;
    border-bottom: solid #cccccc 2px;
    border-radius: 0;
    color: inherit;
    cursor: pointer;
    display: inline;
    font-size: 1em;
    margin-left: 0.5em;
    padding: 0.3em 2em 0.3em 0.3em;
    outline: none;
  }

  select::after {
    content: "v";
  }

  select::-ms-expand {
    display: none;
  }

  select + span::after {
    content: "";
    position: relative;
    border-color: #cccccc;
    transform: rotate(45deg) translate(-0.8em, 0.6em);
    border-style: solid;
    border-width: 0 2px 2px 0;
    width: 0.4em;
    height: 0.4em;
    display: inline-block;
    pointer-events: none;
  }
`;

const Toggle = ({ setChoroplethSelection, choroplethSelection, timeframe }) => {
  return (
    <ToggleWrapper>
      Map:
      <select onChange={evt => setChoroplethSelection(evt.target.value)} value={choroplethSelection}>
        <option value="postings">Postings</option>
        <option value="change">% Change {timeframe} over {timeframe}</option>
        <option value="yearChange">% Change from last year</option>
      </select>
      <span></span>
    </ToggleWrapper>
  );
};

const LegendWrapper = styled.div`
  align-items: stretch;
  display: flex;
  flex-direction: row;
  margin: auto;
  max-width: calc(100% - 5ch);
  position: relative;
  top: -1rem;

  & > div {
    flex: 1;
    height: 0.8rem;
    position: relative;
  }

  & > div > span {
    display: inline-block;
    font-size: 12px;
    left: 0;
    position: absolute;
    top: -16px;
    transform: translateX(-50%);
    line-height: 12px;
  }

  @media only screen and (max-width: 935px) {
    & > div > span {
      font-size: 10px;
    }
  }

  & > div > span::after {
    border-right: solid #204354 1px;
    bottom: calc(-4px - 0.8rem);
    content: "";
    height: calc(2px + 0.8rem);
    left: 50%;
    position: absolute;
  }

  & > div > span + span {
    left: auto;
    right: 1px;
    transform: translateX(50%);
  }
`;

const Legend = ({ type, scale, values }) => {
  const visibleColors = values.map(scale);
  const visibleRange = scale.range().filter(c => visibleColors.includes(c));
  const format = x => type === "int"
    ? Math.round(x).toLocaleString()
    : prettyPercent(x);
  
  return <LegendWrapper>
    {visibleRange.map((c, i) => <div key={c} style={{ background: c }}>
      <span>{format(scale.invertExtent(c)[0])}</span>
      {i === visibleRange.length - 1
        ? <span>{format(scale.invertExtent(c)[1])}</span>
        : null}
    </div>)}
  </LegendWrapper>;
};

const Map = ({ nation, filters, timeframe, latestDay, setTooltipContent }) => {
  const [data, setData] = useState({ data: {} });
  const [choroplethSelection, setChoroplethSelection] = useState("change");

  useEffect(() => {
    let cancel = false;

    const getData = async () => {
      const { currWhen, prevWhen, yearAgoWhen } = getWhens(timeframe, latestDay);

      const facet = {
        us: "state",
        ca: "province"
      }[nation];

      const res = await Promise.all([currWhen, prevWhen, yearAgoWhen].map(when => microApi.post(`/${nation}/rankings/${facet}${nation === "us" ? "?soc_version=soc_emsi_2019" : ""}`, {
        filter: { when, ...filters },
        rank: { limit: 100 }
      })));

      const newData = {};

      res[0].data.data.ranking.buckets.forEach(b => {
        const prevBucket = res[1].data.data.ranking.buckets
          .find(b2 => b2.name === b.name);
        const lastYearBucket = res[2].data.data.ranking.buckets
          .find(b2 => b2.name === b.name);

        newData[b.name] = {
          id: b.name,
          postings: b.unique_postings,
          change: prevBucket
            ? percentChange(prevBucket.unique_postings, b.unique_postings)
            : undefined,
          yearChange: lastYearBucket
            ? percentChange(lastYearBucket.unique_postings, b.unique_postings)
            : undefined
        };
      });

      if (cancel) return;
      setData({
        data: newData,
        timeframe
      });
    };
    getData();
    return () => (cancel = true);
  }, [filters, timeframe, latestDay, nation]);

  const values = Object.values(data.data).map(state => state[choroplethSelection]);
  const scale = getColorScale(values);

  const projection = {
    us: {
      name: "geoAlbersUsa"
    },
    ca: {
      name: "geoConicConformal",
      config: {
        rotate: [98, 0],
        center: [0, 66],
        parallels: [-10, 85.5],
        scale: 1000
      }
    }
  }[nation];

  return (
    <div id="pingdom-map-check" style={{ position: "relative", marginBottom: "1rem" }}>
      <Toggle timeframe={data.timeframe || timeframe} {...{ choroplethSelection, setChoroplethSelection }} />
      <ComposableMap data-tip="" projection={projection.name} projectionConfig={projection.config}>
        <Geographies geography={`/${nation}-topo.json`}>
          {({ geographies }) => (
            <>
              {geographies.map(geo => {
                const state = data.data && data.data[parseInt(geo.id || geo.properties.id)];
                return (
                  <Geography
                    key={geo.rsmKey}
                    stroke="#fff"
                    geography={geo}
                    onMouseEnter={() => {
                      if (state) {
                        setTooltipContent(
                          <>
                            <span>{geo.properties.name}</span>
                            <br />
                            {state.postings.toLocaleString()} postings*
                            <br />
                            {prettyPercent(state.change)} {data.timeframe} over {data.timeframe}
                            <br />
                            {prettyPercent(state.yearChange)} from last year
                          </>
                        );
                      }
                    }}
                    onMouseLeave={() => {
                      setTooltipContent("");
                    }}
                    style={{
                      default: {
                        outline: "none"
                      },
                      hover: {
                        opacity: 0.5,
                        outline: "none"
                      },
                      pressed: {
                        opacity: 0.5,
                        outline: "none"
                      },
                    }}
                    fill={state ? scale(state[choroplethSelection]) : "#eee"}
                  />
                );
              })}
            </>
          )}
        </Geographies>
      </ComposableMap>
      <Legend scale={scale} values={values} type={choroplethSelection === "postings" ? "int" : "pct"} />
    </div>
  );
};

export default Map;
